import { useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { InputSwitch } from 'primereact/inputswitch'
import { confirmDialog } from 'primereact/confirmdialog'
import { z } from 'zod'
import type { ConditionTree } from '@kc3/conversations'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, {
  type ColumnDef,
} from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import {
  ZodForm,
  FormText,
  FormNumber,
  FormSwitch,
  FormDropdown,
} from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'
import { useRuleFields } from '../../../hooks/useRuleFields.js'
import RuleBuilder from '../../../components/admin/conversation-engine/RuleBuilder.js'
import AutomationActionPicker, {
  type AutomationAction,
} from '../../../components/admin/conversation-engine/AutomationActionPicker.js'
import ScheduleEditor, {
  type Schedule,
} from '../../../components/admin/ScheduleEditor.js'
import TriggerTypeBadge from '../../../components/admin/conversation-engine/TriggerTypeBadge.js'
import RuleTestSidebar from '../../../components/admin/conversation-engine/RuleTestSidebar.js'
import { formatDate } from '../../../utils/formatDate.js'

// ---------------------------------------------------------------------------
// Types — derived from apps/api/src/routes/conversations/admin/automation-rules.ts
// CreateBodySchema.TriggerTypeEnum (15 values). NOTE: `on_outbound_message_drafted`
// is fired by the engine (UI-1e send-message.ts) but NOT in the route enum, so it
// is INTENTIONALLY excluded from the trigger dropdown — admin-authored rules with
// that trigger would be rejected by the route at POST time. See plan §0 / Group A
// CRITICAL CROSS-SEAM #1 for context. TriggerTypeBadge still labels it for any
// rule that somehow displays it.
// ---------------------------------------------------------------------------

export interface AutomationRule {
  id: string
  name: string
  triggerType: TriggerType
  conditions: ConditionTree | null
  actions: AutomationAction[] | null
  isActive: boolean
  runOncePerConversation: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
}

type TriggerType =
  | 'on_create'
  | 'on_update'
  | 'on_message_received'
  | 'on_message_sent'
  | 'on_note_added'
  | 'on_sla_breach'
  | 'on_ola_breach'
  | 'on_sla_threshold'
  | 'on_status_change'
  | 'on_assignment_change'
  | 'on_approval_decision'
  | 'on_transfer'
  | 'on_escalation'
  | 'on_merge'
  | 'scheduled'

const TRIGGER_OPTIONS: ReadonlyArray<{ label: string; value: TriggerType }> = [
  { label: 'On create', value: 'on_create' },
  { label: 'On update', value: 'on_update' },
  { label: 'On inbound message', value: 'on_message_received' },
  { label: 'On message sent', value: 'on_message_sent' },
  { label: 'On note added', value: 'on_note_added' },
  { label: 'On SLA breach', value: 'on_sla_breach' },
  { label: 'On OLA breach', value: 'on_ola_breach' },
  { label: 'On SLA threshold', value: 'on_sla_threshold' },
  { label: 'On status change', value: 'on_status_change' },
  { label: 'On assignment change', value: 'on_assignment_change' },
  { label: 'On approval decision', value: 'on_approval_decision' },
  { label: 'On transfer', value: 'on_transfer' },
  { label: 'On escalation', value: 'on_escalation' },
  { label: 'On merge', value: 'on_merge' },
  { label: 'Scheduled', value: 'scheduled' },
]

// Stable module-level constants (avoid fresh refs each render).
const EMPTY_CONDITIONS: ConditionTree = { operator: 'AND', conditions: [] }
const EMPTY_ACTIONS: AutomationAction[] = []

const RuleSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  triggerType: z.enum([
    'on_create',
    'on_update',
    'on_message_received',
    'on_message_sent',
    'on_note_added',
    'on_sla_breach',
    'on_ola_breach',
    'on_sla_threshold',
    'on_status_change',
    'on_assignment_change',
    'on_approval_decision',
    'on_transfer',
    'on_escalation',
    'on_merge',
    'scheduled',
  ]),
  isActive: z.boolean(),
  runOncePerConversation: z.boolean(),
  sortOrder: z.number().int().min(0).max(32767),
})

type RuleFormValues = z.infer<typeof RuleSchema>

function summariseActions(actions: AutomationAction[] | null): string {
  if (!actions || actions.length === 0) return '—'
  return `${actions.length} action${actions.length === 1 ? '' : 's'}`
}

export default function AutomationRulesPage(): JSX.Element {
  const { items, loading, create, update, remove } = useAdminResource<AutomationRule>(
    '/admin/conversations/automation-rules',
    { responseKey: 'rows' },
  )
  const { fields } = useRuleFields('automation')

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<AutomationRule | null>(null)
  const [conditions, setConditions] = useState<ConditionTree>(EMPTY_CONDITIONS)
  const [actions, setActions] = useState<AutomationAction[]>(EMPTY_ACTIONS)
  const [schedule, setSchedule] = useState<Schedule | null>(null)
  const [showTest, setShowTest] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  function openCreate(): void {
    setEditing(null)
    setConditions(EMPTY_CONDITIONS)
    setActions(EMPTY_ACTIONS)
    setSchedule(null)
    setShowTest(false)
    setDrawerOpen(true)
  }

  function openEdit(row: AutomationRule): void {
    setEditing(row)
    setConditions(row.conditions ?? EMPTY_CONDITIONS)
    setActions((row.actions as AutomationAction[]) ?? EMPTY_ACTIONS)
    setSchedule(null)
    setShowTest(false)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
  }

  async function handleSubmit(data: RuleFormValues): Promise<void> {
    // Schedule persistence decision (UI-2c Group B): the @kc3/api
    // automation-rules CreateBodySchema is `z.object({...})` WITHOUT
    // .passthrough() — unknown keys are stripped by zod. We therefore do NOT
    // include `schedule` in the POST/PATCH body. The ScheduleEditor below is
    // rendered when triggerType === 'scheduled' with an inline notice that
    // persistence wiring lands in a follow-up (UI-2d/2e).
    const payload = {
      name: data.name.trim(),
      triggerType: data.triggerType,
      conditions,
      actions,
      isActive: data.isActive,
      runOncePerConversation: data.runOncePerConversation,
      sortOrder: data.sortOrder,
    }
    if (editing) {
      await update(editing.id, payload)
    } else {
      await create(payload)
    }
    closeDrawer()
  }

  function handleDelete(row: AutomationRule): void {
    confirmDialog({
      message: `Permanently delete "${row.name}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  const columns: ColumnDef<AutomationRule>[] = [
    { label: 'Name', accessor: 'name' },
    {
      label: 'Trigger',
      accessor: (r) => <TriggerTypeBadge triggerType={r.triggerType} />,
    },
    {
      label: 'Actions',
      accessor: (r) => summariseActions(r.actions),
    },
    {
      label: 'Active',
      accessor: (r) => (
        <InputSwitch
          checked={r.isActive}
          onChange={(e) => void update(r.id, { isActive: Boolean(e.value) })}
        />
      ),
    },
    {
      label: 'Updated',
      accessor: (r) => formatDate(r.updatedAt),
    },
  ]

  const defaultValues: RuleFormValues = editing
    ? {
        name: editing.name,
        triggerType: editing.triggerType,
        isActive: editing.isActive,
        runOncePerConversation: editing.runOncePerConversation,
        sortOrder: editing.sortOrder,
      }
    : {
        name: '',
        triggerType: 'on_create',
        isActive: true,
        runOncePerConversation: false,
        sortOrder: 100,
      }

  return (
    <div className="p-4">
      <PageHeader
        title="Automation Rules"
        description="Event- and time-triggered automations. Conditions + actions evaluated by the engine."
        actions={<Button label="+ New Rule" onClick={openCreate} />}
      />
      <ResourceTable<AutomationRule>
        columns={columns}
        rows={items}
        loading={loading}
        onEdit={openEdit}
        onDelete={handleDelete}
        empty={{
          title: 'No automation rules',
          description: 'Create a rule to react to conversation events.',
          action: <Button label="+ New Rule" onClick={openCreate} />,
        }}
      />
      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        width="wide"
        title={editing ? `Edit Rule: ${editing.name}` : 'New Automation Rule'}
      >
        <ZodForm
          schema={RuleSchema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          render={(methods) => {
            const watchedTrigger = methods.watch('triggerType') as TriggerType
            return (
              <>
                <FormText name="name" label="Name" required autoFocus />
                <FormDropdown
                  name="triggerType"
                  label="Trigger"
                  options={[...TRIGGER_OPTIONS]}
                  required
                />
                <div className="mb-3">
                  <TriggerTypeBadge triggerType={watchedTrigger} />
                </div>
                <FormSwitch name="isActive" label="Active" />
                <FormSwitch
                  name="runOncePerConversation"
                  label="Run once per conversation"
                />
                <FormNumber name="sortOrder" label="Sort order" />

                {watchedTrigger === 'scheduled' && (
                  <div className="mt-4">
                    <h3 className="mb-2 text-sm font-semibold">Schedule</h3>
                    <ScheduleEditor
                      value={schedule}
                      onChange={setSchedule}
                    />
                    <p
                      role="note"
                      className="mt-1 text-xs text-amber-700"
                      data-testid="schedule-persistence-notice"
                    >
                      Schedule persistence wiring lands in a follow-up — the
                      rule will save with trigger &quot;scheduled&quot; but
                      the schedule value is not yet posted to the API
                      (CreateBodySchema strips unknown keys).
                    </p>
                  </div>
                )}

                <div className="mt-4">
                  <h3 className="mb-2 text-sm font-semibold">Conditions</h3>
                  <RuleBuilder
                    value={conditions}
                    onChange={setConditions}
                    fields={fields}
                  />
                </div>

                <div className="mt-4">
                  <h3 className="mb-2 text-sm font-semibold">Actions</h3>
                  <AutomationActionPicker
                    value={actions}
                    onChange={setActions}
                    fields={fields}
                  />
                </div>

                <div className="mt-4">
                  <button
                    type="button"
                    className="rounded border border-gray-300 px-2 py-1 text-xs"
                    onClick={() => setShowTest((v) => !v)}
                  >
                    {showTest ? 'Hide test sidebar' : 'Show test sidebar'}
                  </button>
                  {showTest && (
                    <div className="mt-2">
                      <RuleTestSidebar
                        rule={{ conditions, actions }}
                        domain="automation"
                      />
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className="mt-4 rounded bg-[#16273F] px-4 py-2 text-white"
                >
                  Save
                </button>
              </>
            )
          }}
        />
      </ResourceDrawer>
    </div>
  )
}

// Re-export for tests / external consumers.
export { TRIGGER_OPTIONS }
